// Labels and error wording of Data Perusahaan (/data-perusahaan) and the admin
// Grup Perusahaan list. The API already answers in Indonesian; the codes of
// its 403, 404 and 409 answers get one clear sentence each here, so the
// screen reads the same whatever the server text was.
import * as API from "../api";
import type { CompanyDocKind, CompanyDocType, CompanyDocument, CompanyGroupRole } from "../api";

export const DOC_TYPE_LABEL: Record<CompanyDocType, string> = {
  nib: "NIB",
  akta: "Akta perusahaan",
  npwp: "NPWP",
  sbu: "SBU",
  siup: "Izin usaha",
  kbli: "KBLI",
  pengalaman: "Pengalaman perusahaan",
  cv_tenaga_ahli: "CV tenaga ahli",
  laporan_keuangan: "Laporan keuangan",
  profil_perusahaan: "Profil perusahaan",
  lainnya: "Dokumen lain",
};

/** The select order: the dokumen persyaratan first, Dokumen lain last. */
export const DOC_TYPES = Object.keys(DOC_TYPE_LABEL) as CompanyDocType[];

export const KIND_LABEL: Record<CompanyDocKind, string> = {
  persyaratan: "Persyaratan",
  umum: "Umum",
};

export const ROLE_LABEL: Record<CompanyGroupRole, string> = {
  owner: "Pemilik",
  admin: "Admin",
  member: "Anggota",
};

export const INDEX_LABEL: Record<CompanyDocument["indexStatus"], string> = {
  pending: "Diproses",
  indexed: "Siap dipakai Pluto",
  failed: "Gagal diproses",
};

export const INDEX_TONE: Record<CompanyDocument["indexStatus"], "info" | "success" | "danger"> = {
  pending: "info",
  indexed: "success",
  failed: "danger",
};

/** Word, PDF, Excel or plain text, at most 10 MB (as the API checks). */
export const UPLOAD_ACCEPT = ".docx,.pdf,.xlsx,.txt";
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** Why a picked file cannot be uploaded, or null. */
export function uploadProblem(file: File): string | null {
  if (!/\.(docx|pdf|xlsx|txt)$/i.test(file.name)) return "Unggah file Word (.docx), PDF, Excel (.xlsx), atau teks (.txt).";
  if (file.size > UPLOAD_MAX_BYTES) return "Ukuran file maksimal 10 MB.";
  if (file.size === 0) return "File kosong. Pilih file yang berisi teks.";
  return null;
}

/** One sentence per error code of /company. */
export const COMPANY_ERRORS: Record<string, string> = {
  already_in_group: "Akun Anda sudah tergabung dalam grup perusahaan. Keluar dari grup itu terlebih dahulu.",
  owner_cannot_leave: "Pemilik grup tidak dapat keluar. Jadikan anggota lain pemilik terlebih dahulu, atau hapus grup.",
  already_member: "Akun dengan email ini sudah menjadi anggota grup.",
  not_in_group: "Anda tidak lagi tergabung dalam grup perusahaan. Halaman dimuat ulang.",
  not_group_manager: "Hanya pemilik atau admin grup yang dapat melakukan ini.",
  not_group_owner: "Hanya pemilik grup yang dapat melakukan ini.",
  not_document_manager: "Hanya pengunggah atau pemilik dan admin grup yang dapat mengubah dokumen ini.",
  cannot_remove_owner: "Pemilik grup tidak dapat dikeluarkan.",
  use_leave: "Gunakan Keluar dari grup untuk akun Anda sendiri.",
  use_transfer: "Peran pemilik diganti dengan menjadikan anggota lain pemilik.",
  group_full: "Grup sudah mencapai batas anggota.",
  too_many_invites: "Grup sudah memiliki terlalu banyak undangan terbuka. Batalkan undangan yang tidak dipakai.",
  too_many_documents: "Batas jumlah dokumen perusahaan tercapai. Hapus dokumen yang tidak dipakai lagi.",
  file_too_large: "Ukuran file maksimal 10 MB.",
  unsupported_type: "Unggah file Word (.docx), PDF, Excel (.xlsx), atau teks (.txt).",
  feature_disabled: "Data Perusahaan tidak tersedia untuk peran Anda.",
};

export function errorCode(err: unknown): string | null {
  if (!(err instanceof API.ApiError)) return null;
  const code = err.body?.code;
  return typeof code === "string" ? code : null;
}

/** The clear sentence for an error of /company. */
export function companyErrorMessage(err: unknown): string {
  const code = errorCode(err);
  if (code && COMPANY_ERRORS[code]) return COMPANY_ERRORS[code];
  if (err instanceof API.ApiError && err.status === 404) {
    const base = err.message.replace(/\.$/, "");
    return `${base}. Data mungkin sudah diubah anggota lain; halaman dimuat ulang.`;
  }
  return API.errorMessage(err);
}

/** True when the screen should load the group and documents again after this error. */
export function needsReload(err: unknown): boolean {
  if (!(err instanceof API.ApiError)) return false;
  if (err.status === 404) return true;
  const code = errorCode(err);
  return code === "already_in_group" || code === "not_group_manager" || code === "not_group_owner";
}
